import React, { useState } from 'react';
import {
    Table, Button, Drawer, Form, Input, Space, Typography, Spin, Alert, Tag, Popconfirm,
    Row, Col,
} from 'antd';
import { PlusOutlined, EditOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
    BranchDTO,
    useBranches,
    useCreateBranch,
    useUpdateBranch,
    useActivateBranch,
    useDeactivateBranch,
} from '../hooks/useBranchManagement';

const { Title } = Typography;

type DrawerMode = 'create' | 'edit';

export const BranchListView: React.FC = () => {
    const { data: branches = [], isLoading, error } = useBranches();
    const create = useCreateBranch();
    const update = useUpdateBranch();
    const activate = useActivateBranch();
    const deactivate = useDeactivateBranch();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [mode, setMode] = useState<DrawerMode>('create');
    const [editCode, setEditCode] = useState<string | null>(null);
    const [form] = Form.useForm<BranchDTO>();

    const openCreate = () => {
        setMode('create');
        setEditCode(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEdit = (record: BranchDTO) => {
        setMode('edit');
        setEditCode(record.branch);
        form.setFieldsValue(record);
        setDrawerOpen(true);
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        if (mode === 'create') {
            create.mutate(values, { onSuccess: () => setDrawerOpen(false) });
        } else if (editCode) {
            update.mutate({ ...values, branch: editCode }, { onSuccess: () => setDrawerOpen(false) });
        }
    };

    const isMutating = create.isPending || update.isPending;

    const columns: ColumnsType<BranchDTO> = [
        { title: 'Code', dataIndex: 'branch', key: 'branch', width: 70 },
        { title: 'Full Name', dataIndex: 'brFname', key: 'brFname' },
        { title: 'Short Name', dataIndex: 'brName', key: 'brName', width: 120 },
        { title: 'Region', dataIndex: 'region', key: 'region', width: 130 },
        { title: 'Manager', dataIndex: 'managerName', key: 'managerName', width: 150 },
        {
            title: 'Status',
            dataIndex: 'brsesStat',
            key: 'brsesStat',
            width: 100,
            render: (stat: string) =>
                stat === 'A' ? (
                    <Tag color="green">Active</Tag>
                ) : stat === 'I' ? (
                    <Tag color="red">Inactive</Tag>
                ) : (
                    <Tag>{stat || '—'}</Tag>
                ),
        },
        {
            title: 'Session Date',
            dataIndex: 'brsesDate',
            key: 'brsesDate',
            width: 130,
            render: (v: string) => v ? new Date(v).toLocaleDateString() : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)}>
                        Edit
                    </Button>
                    {record.brsesStat !== 'A' ? (
                        <Popconfirm
                            title="Activate this branch?"
                            onConfirm={() => activate.mutate(record.branch)}
                        >
                            <Button icon={<CheckCircleOutlined />} size="small" type="primary" ghost>
                                Activate
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this branch?"
                            onConfirm={() => deactivate.mutate(record.branch)}
                        >
                            <Button icon={<StopOutlined />} size="small" danger>
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    if (error) return <Alert type="error" message="Failed to load branches" />;

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>Branch Management</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    New Branch
                </Button>
            </Space>

            <Spin spinning={isLoading}>
                <Table
                    rowKey="branch"
                    columns={columns}
                    dataSource={branches}
                    pagination={false}
                    size="small"
                />
            </Spin>

            <Drawer
                title={mode === 'create' ? 'Create Branch' : `Edit Branch: ${editCode}`}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={560}
                footer={
                    <Space>
                        <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" loading={isMutating} onClick={handleSave}>
                            Save
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical">
                    {mode === 'create' && (
                        <Form.Item
                            label="Branch Code"
                            name="branch"
                            rules={[
                                { required: true, message: 'Branch code is required' },
                                { max: 3, message: 'Max 3 characters' },
                            ]}
                        >
                            <Input maxLength={3} placeholder="e.g. HQ0" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                    )}

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Full Name" name="brFname">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Short Name" name="brName">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Registration No" name="regNo">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Telephone" name="telephone">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Mobile" name="mobile">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Email" name="email">
                                <Input type="email" maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Address" name="address">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Region" name="region">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="District" name="district">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Manager Name" name="managerName">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Contact Person" name="contactPerson">
                                <Input maxLength={254} />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Opening Hours" name="openingHours">
                                <Input maxLength={254} placeholder="e.g. Mon-Fri 8am-5pm" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="GPS Coordinates" name="gpsCoordinates">
                                <Input maxLength={254} placeholder="e.g. -6.7924, 39.2083" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Services Offered" name="servicesOffered">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </div>
    );
};
